import { type MaybeRef, computed, toRef } from "vue";

import { useQuery } from "@tanstack/vue-query";

import { booleanToBinary } from "@/helpers/common";
import {
  ANALYST,
  CLIENT_ANALYST,
  CLIENT_FUNDING_ADVISOR,
  CLIENT_UNDERWRITER,
  FUNDING_ADVISOR,
  ONE_MINUTE_MS,
  UNDERWRITER,
} from "@/helpers/constants";
import { QUERY_KEYS } from "@/helpers/constants/queryKeys";
import usersApiService from "@/services/modules/users";

import type { IClient } from "@/models/clients";
import type { Role } from "@/models/options";
import type { IUser } from "@/models/users";
import type { GetUsersByRoleParams } from "@/services/modules/users";

const FIVE_MINUTES_MS = 5 * ONE_MINUTE_MS;
const TEN_MINUTES_MS = 10 * ONE_MINUTE_MS;

interface UseUsersByRoleOptions {
  ignoreAssignedDeals?: boolean;
  enabled?: MaybeRef<boolean>;
}

interface UseClientUsersByRoleOptions extends UseUsersByRoleOptions {
  clientId?: IClient["id"];
}

export const useUsersByRole = (
  role: Role,
  options: UseClientUsersByRoleOptions = {}
) => {
  const { ignoreAssignedDeals, clientId, enabled = true } = options;
  return useQuery({
    queryKey: computed(() =>
      QUERY_KEYS.USERS.byRole(role, ignoreAssignedDeals, clientId)
    ),
    queryFn: async (): Promise<IUser[]> => {
      const servicePayload: GetUsersByRoleParams = { role };
      if (ignoreAssignedDeals !== undefined) {
        servicePayload.ignore_assigned_deals =
          booleanToBinary(ignoreAssignedDeals);
      }
      if (clientId) {
        servicePayload.client_id = clientId;
      }
      const response = await usersApiService.getUsersByRole(servicePayload);
      return response;
    },
    enabled: toRef(enabled),
    staleTime: FIVE_MINUTES_MS,
    gcTime: TEN_MINUTES_MS,
  });
};

export const useAnalystsQuery = (options: UseUsersByRoleOptions = {}) => {
  return useUsersByRole(ANALYST, options);
};

export const useFundingAdvisorsQuery = (
  options: UseUsersByRoleOptions = {}
) => {
  return useUsersByRole(FUNDING_ADVISOR, options);
};

export const useUnderwritersQuery = (options: UseUsersByRoleOptions = {}) => {
  return useUsersByRole(UNDERWRITER, options);
};

export const useClientAnalystsQuery = (
  options: UseClientUsersByRoleOptions = {}
) => {
  return useUsersByRole(CLIENT_ANALYST, options);
};

export const useClientFundingAdvisorsQuery = (
  options: UseClientUsersByRoleOptions = {}
) => {
  return useUsersByRole(CLIENT_FUNDING_ADVISOR, options);
};

export const useClientUnderwritersQuery = (
  options: UseClientUsersByRoleOptions = {}
) => {
  return useUsersByRole(CLIENT_UNDERWRITER, options);
};
